import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { trFold, areaNum, prodStatusCls, saleStatusCls } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Search, Factory, FolderOpen, Ruler, Banknote, Wallet, TrendingUp } from "lucide-react";

const fmt = (n, d = 0) => (n || 0).toLocaleString("tr-TR", { maximumFractionDigits: d });

const sumByCur = (list, amountKey, curKey) => {
  const t = {};
  list.forEach((p) => {
    const a = p[amountKey];
    if (a > 0) {
      const c = p[curKey] || "TRY";
      t[c] = (t[c] || 0) + a;
    }
  });
  return t;
};
const toTL = (t, rates) => (rates?.USD && rates?.EUR ? (t.TRY || 0) + (t.USD || 0) * rates.USD + (t.EUR || 0) * rates.EUR : null);
const curParts = (t) => ["USD", "EUR", "TRY"].filter((c) => t[c]).map((c) => `${fmt(t[c])} ${c === "TRY" ? "TL" : c}`).join(" · ");

const MoneyBlock = ({ label, totals, rates, testid }) => {
  const tl = toTL(totals, rates);
  const parts = curParts(totals);
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="font-heading font-extrabold tabular-nums text-lg leading-tight">
        {tl != null ? `₺${fmt(tl)}` : parts || "—"}
      </div>
      {tl != null && <div className="text-[10px] text-slate-400 tabular-nums">{parts || "kayıt yok"}</div>}
    </div>
  );
};

const ProfitBlock = ({ projects, rates, testid, big = false }) => {
  const offer = sumByCur(projects, "offer_amount", "offer_currency");
  const price = sumByCur(projects, "producer_price", "producer_price_currency");
  const o = toTL(offer, rates);
  const c = toTL(price, rates);
  const hasBoth = Object.keys(offer).length > 0 && Object.keys(price).length > 0;
  if (!hasBoth || o == null || c == null) {
    return (
      <div data-testid={testid}>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Kâr Marjı</div>
        <div className={`font-heading font-extrabold tabular-nums ${big ? "text-2xl" : "text-lg"} text-slate-300`}>—</div>
        <div className="text-[10px] text-slate-400">{Object.keys(price).length === 0 ? "Üretici fiyatı girilmemiş" : "Teklif tutarı girilmemiş"}</div>
      </div>
    );
  }
  const diff = o - c;
  const pct = o > 0 ? (diff / o) * 100 : null;
  const pos = diff >= 0;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Kâr Marjı</div>
      <div className={`font-heading font-extrabold tabular-nums ${big ? "text-2xl" : "text-lg"} leading-tight ${pos ? "text-emerald-600" : "text-red-600"}`}>
        {pos ? "+" : "−"}₺{fmt(Math.abs(diff))}
      </div>
      {pct != null && (
        <div className={`text-[10px] font-semibold tabular-nums ${pos ? "text-emerald-600" : "text-red-600"}`}>
          {pos ? "+" : "−"}%{fmt(Math.abs(pct), 1)}
        </div>
      )}
    </div>
  );
};

export default function ProducerDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [zone, setZone] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/assigner/supplier-dashboard").then((r) => setData(r.data)).catch((e) => setErr(e?.response?.status === 403 ? "Bu sayfaya erişim yetkiniz yok." : "Veriler yüklenemedi."));
  }, []);

  const rates = data?.rates;
  const zones = useMemo(() => [...new Set((data?.projects || []).map((p) => p.zone).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [data]);

  const cards = useMemo(() => {
    if (!data) return [];
    const projs = data.projects.filter((p) => zone === "all" || p.zone === zone);
    const byProducer = {};
    projs.forEach((p) => { (byProducer[p.producer_id] = byProducer[p.producer_id] || []).push(p); });
    const term = trFold(q.trim());
    return data.producers
      .map((u) => ({
        ...u,
        label: u.company_name || u.contact_name || "Üretici",
        sub: u.company_name && u.contact_name ? u.contact_name : "",
        projects: (byProducer[u.id] || []).sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr", { sensitivity: "base" })),
      }))
      .filter((u) => u.projects.length > 0)
      .filter((u) => !term || trFold(u.label).includes(term) || trFold(u.sub).includes(term))
      .sort((a, b) => b.projects.length - a.projects.length || a.label.localeCompare(b.label, "tr", { sensitivity: "base" }));
  }, [data, q, zone]);

  const det = selected ? cards.find((c) => c.id === selected) : null;

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="supplier-dashboard-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><Factory size={10} /> Tedarikçi Dashboard</div>
          </div>
          <button
            onClick={() => (det ? setSelected(null) : navigate("/atayici"))}
            data-testid="supplier-dash-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150"
          >
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        {err ? (
          <div className="border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="supplier-dash-error">{err}</div>
        ) : !data ? (
          <div className="border border-slate-200 bg-white p-10 text-center text-slate-400">Yükleniyor...</div>
        ) : det ? (
          <>
            <div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight" data-testid="supplier-detail-title">{det.label}</h1>
              {det.sub && <p className="text-sm text-slate-500 mt-0.5">{det.sub}{det.phone ? ` · ${det.phone}` : ""}</p>}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="supplier-detail-kpis">
              {[
                { icon: FolderOpen, label: "Proje Sayısı", val: det.projects.length, accent: "bg-blue-500/10 text-blue-600" },
                { icon: Ruler, label: "Toplam m²", val: fmt(det.projects.reduce((s, p) => s + areaNum(p.area_m2), 0), 1), accent: "bg-emerald-500/10 text-emerald-600" },
              ].map((k) => (
                <div key={k.label} className="bg-white border border-slate-200 p-5">
                  <div className={`w-9 h-9 flex items-center justify-center ${k.accent}`}><k.icon size={16} /></div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-3">{k.label}</div>
                  <div className="font-heading text-2xl font-extrabold tabular-nums">{k.val}</div>
                </div>
              ))}
              <div className="bg-white border border-slate-200 p-5">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-500/10 text-amber-600"><Banknote size={16} /></div>
                <div className="mt-3"><MoneyBlock label="Teklif Tutarı Toplamı" totals={sumByCur(det.projects, "offer_amount", "offer_currency")} rates={rates} testid="supplier-detail-offer" /></div>
              </div>
              <div className="bg-white border border-slate-200 p-5">
                <div className="w-9 h-9 flex items-center justify-center bg-purple-500/10 text-purple-600"><Wallet size={16} /></div>
                <div className="mt-3"><MoneyBlock label="Üretici Fiyatı Toplamı" totals={sumByCur(det.projects, "producer_price", "producer_price_currency")} rates={rates} testid="supplier-detail-price" /></div>
              </div>
              <div className="bg-white border border-slate-200 p-5">
                <div className="w-9 h-9 flex items-center justify-center bg-emerald-500/10 text-emerald-600"><TrendingUp size={16} /></div>
                <div className="mt-3"><ProfitBlock projects={det.projects} rates={rates} testid="supplier-detail-profit" big /></div>
              </div>
            </div>
            <div className="border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
                    <th className="px-4 py-3 font-medium">Proje</th>
                    <th className="px-4 py-3 font-medium">Bölge</th>
                    <th className="px-4 py-3 font-medium">Stand</th>
                    <th className="px-4 py-3 font-medium text-right">m²</th>
                    <th className="px-4 py-3 font-medium">Üretim Durumu</th>
                    <th className="px-4 py-3 font-medium text-right">Teklif Tutarı</th>
                    <th className="px-4 py-3 font-medium text-right">Üretici Fiyatı</th>
                  </tr>
                </thead>
                <tbody data-testid="supplier-detail-rows">
                  {det.projects.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-4 py-3 font-semibold">{p.name}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{p.zone}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{p.stand_no || "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{areaNum(p.area_m2) ? fmt(areaNum(p.area_m2), 1) : "—"}</td>
                      <td className="px-4 py-3">
                        {p.production_status
                          ? <Badge variant="outline" className={`rounded-none text-[10px] ${prodStatusCls(p.production_status)}`}>{p.production_status}</Badge>
                          : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-xs font-semibold">
                        {p.offer_amount > 0 ? `${fmt(p.offer_amount)} ${p.offer_currency === "TRY" || !p.offer_currency ? "TL" : p.offer_currency}` : <span className="text-slate-300 font-normal">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-xs font-semibold">
                        {p.producer_price > 0 ? `${fmt(p.producer_price)} ${p.producer_price_currency === "TRY" || !p.producer_price_currency ? "TL" : p.producer_price_currency}` : <span className="text-slate-300 font-normal">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="font-heading text-3xl font-extrabold tracking-tight">Tedarikçiler</h1>
                <p className="text-sm text-slate-500 mt-1">Proje atanan üreticiler — {cards.length} tedarikçi</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {zones.length > 1 && (
                  <Select value={zone} onValueChange={setZone}>
                    <SelectTrigger data-testid="supplier-dash-zone" className="rounded-none h-10 w-[160px] bg-white border-slate-200 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none">
                      <SelectItem value="all">Tüm Bölgeler</SelectItem>
                      {zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tedarikçi ara..." data-testid="supplier-dash-search" className="rounded-none h-10 pl-9 w-[220px] bg-white border-slate-200" />
                </div>
              </div>
            </div>
            {cards.length === 0 ? (
              <div className="border border-slate-200 bg-white p-12 text-center text-slate-400" data-testid="supplier-dash-empty">
                {q || zone !== "all" ? "Aramanızla eşleşen tedarikçi yok." : "Henüz proje atanmış üretici yok."}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="supplier-dash-grid">
                {cards.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelected(c.id)}
                    data-testid={`supplier-card-${c.id}`}
                    className="text-left bg-white border border-slate-200 p-5 hover:border-slate-900 hover:shadow-sm transition-all duration-150 group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-base tracking-tight truncate group-hover:underline">{c.label}</div>
                        {c.sub && <div className="text-xs text-slate-400 truncate">{c.sub}</div>}
                      </div>
                      <Badge variant="outline" className="rounded-none shrink-0 bg-blue-500/10 text-blue-700 border-blue-500/30 text-[10px]" data-testid={`supplier-card-count-${c.id}`}>
                        {c.projects.length} proje
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
                      <MoneyBlock label="Teklif Tutarı" totals={sumByCur(c.projects, "offer_amount", "offer_currency")} rates={rates} testid={`supplier-card-offer-${c.id}`} />
                      <MoneyBlock label="Üretici Fiyatı" totals={sumByCur(c.projects, "producer_price", "producer_price_currency")} rates={rates} testid={`supplier-card-price-${c.id}`} />
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <ProfitBlock projects={c.projects} rates={rates} testid={`supplier-card-profit-${c.id}`} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
